/**
 * Modello di presentazione della coda (prototipo L134-L159).
 * Puro: nessuna rete, nessun `window`. Il filtro e la paginazione restano
 * SERVER-SIDE come oggi (TicketQueue.tsx:228-256); qui si calcola cosa
 * disegnare e come riordinare la PAGINA CORRENTE.
 */
import { STATUS_ACTIVE, initialsOf } from '../constants';
import { photoUrl } from '../../../utils/photoUrl';
import type { ChannelAvatarLike } from '../channelAvatar';
import {
  originVisual,
  priorityRailColor,
  priorityVisual,
  requesterTypeVisual,
  statusVisual,
  type S3Visual,
} from './visual';
import type { QueueQuickFilter } from './kpiModel';
import type { ListParams, SavedView, SavedViewFilters, TicketListItem } from '../../../services/supportApi';
import { displayStatus, type DisplayStatus } from '../displayStatus';

export const relativeAge = (iso: string, now: Date = new Date()): string => {
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '—';
  const min = Math.floor((now.getTime() - t) / 60000);
  if (min < 1) return 'ora';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d} g`;
  return `${Math.floor(d / 7)} sett`;
};

/** Data breve all'italiana (gg/mm/aaaa, ora locale); '—' se non valida. */
export const shortDateIt = (iso: string | undefined | null): string => {
  const d = iso ? new Date(iso) : null;
  if (!d || isNaN(d.getTime())) return '—';
  const p2 = (n: number) => String(n).padStart(2, '0');
  return `${p2(d.getDate())}/${p2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

/**
 * Canale nei filtri: un canale disattivato compare solo finche ha ticket attivi
 * (o se e quello selezionato), con l'etichetta DISATTIVATO.
 */
export const channelFilterVisible = (
  g: { _id: string; isActive?: boolean; activeTickets?: number },
  selectedId = ''
): boolean => g.isActive !== false || (g.activeTickets || 0) > 0 || (selectedId !== '' && g._id === selectedId);

export const channelFilterLabel = (g: { nome: string; isActive?: boolean }): string =>
  g.isActive === false ? `${g.nome} · DISATTIVATO` : g.nome;

export const countLabel =(total: number): string => `${total} TICKET`;

export const sortLabel = (sort: QueueSort): string =>
  sort === 'unread' ? 'NON LETTI ↓' : 'PRIORITÀ ↓';

export type QueueSort = 'priority' | 'unread';

// ===== Ordinamento di colonna (coda a tabella, lato server) =====

/** Colonne ordinabili della tabella (SLA e Assegnato no). Stesse chiavi del backend (`sort`). */
export type ColumnSortKey = 'number' | 'created' | 'requester' | 'channel' | 'status' | 'priority' | 'activity';
export type SortDir = 'asc' | 'desc';
export interface ColumnSort {
  key: ColumnSortKey;
  dir: SortDir;
}

/**
 * Direzione del primo clic, l'ordine «naturale» di ogni colonna: testo A→Z,
 * date e numero dal piu recente/alto, stato nell'ordine di lavoro (aperto →
 * chiuso), priorita da urgente a bassa (decrescente, come «PRIORITÀ ↓»).
 */
export const COLUMN_SORT_NATURAL: Record<ColumnSortKey, SortDir> = {
  number: 'desc',
  created: 'desc',
  requester: 'asc',
  channel: 'asc',
  status: 'asc',
  priority: 'desc',
  activity: 'desc',
};

const COLUMN_SORT_LABELS: Record<ColumnSortKey, string> = {
  number: 'TICKET',
  created: 'APERTO',
  requester: 'RICHIEDENTE',
  channel: 'CANALE',
  status: 'STATO',
  priority: 'PRIORITÀ',
  activity: 'ATTIVITÀ',
};

/**
 * Clic sull'intestazione: colonna nuova → ordine naturale; stessa colonna
 * nell'ordine naturale → inverso; inverso → nessun ordinamento (si torna
 * all'ordine di lavoro: prima i da leggere, poi l'ultima attivita).
 */
export const nextColumnSort = (current: ColumnSort | null, key: ColumnSortKey): ColumnSort | null => {
  const natural = COLUMN_SORT_NATURAL[key];
  if (!current || current.key !== key) return { key, dir: natural };
  if (current.dir === natural) return { key, dir: natural === 'asc' ? 'desc' : 'asc' };
  return null;
};

/** Valore di `aria-sort` dell'intestazione di colonna. */
export const ariaSortFor = (current: ColumnSort | null, key: ColumnSortKey): 'ascending' | 'descending' | 'none' =>
  current && current.key === key ? (current.dir === 'asc' ? 'ascending' : 'descending') : 'none';

/** Etichetta del bottone d'ordinamento quando e attiva una colonna (es. «DATA ↓»). */
export const columnSortLabel = (sort: ColumnSort): string =>
  `${COLUMN_SORT_LABELS[sort.key]} ${sort.dir === 'asc' ? '↑' : '↓'}`;

/**
 * Una pillola KPI (o un filtro esterno) «sceglie» lo stato solo se NON riporta
 * la coda al default: stato Attivi, senza non letti ne filtro rapido. La
 * pillola ATTIVI lascia quindi la ricerca libera di guardare in tutti gli stati.
 */
export const pillChoosesStatus = (f: { status?: string; unread?: boolean; quick?: string }): boolean =>
  !(f.status === STATUS_ACTIVE && !f.unread && !f.quick);

/**
 * La ricerca guarda in TUTTI gli stati quando lo stato e quello di default
 * (Attivi) e nessuno l'ha scelto: ne l'utente dai Filtri, ne una vista salvata,
 * ne una pillola KPI. Sul tab dei conclusi resta lo stato del tab.
 */
export const searchesAllStates = (
  status: string,
  ctx: { tab: string; search: string; statusChosen?: boolean }
): boolean =>
  ctx.tab !== TAB_CLOSED && ctx.search.trim() !== '' && ctx.statusChosen === false && status === STATUS_ACTIVE;

export interface QueueFilterState {
  status: string;
  priority: string;
  groupId: string;
  origin: string;
  tag: string;
  requesterType: string;
  unreadOnly: boolean;
  /** «Nascondi uniti e indesiderati» (filtro del backend `hideNoise`); assente = spento. */
  hideNoise?: boolean;
}

/** Badge numerico sul bottone "Filtri" (L119): conta i soli scostamenti dal default. */
export const activeFilterCount = (f: QueueFilterState): number =>
  [
    f.status !== STATUS_ACTIVE,
    Boolean(f.priority),
    Boolean(f.groupId),
    Boolean(f.origin),
    Boolean(f.tag),
    Boolean(f.requesterType),
    f.unreadOnly,
    Boolean(f.hideNoise),
  ].filter(Boolean).length;

/** Tab "Risolti e chiusi": solo lato client, il backend riceve `tab=all` + stato CSV. */
export const TAB_CLOSED = 'closed';
export const CLOSED_STATUSES = 'risolto,chiuso';

/** Scheda dei ticket in cui l'operatore è partecipante invitato (parte B). */
export const TAB_INVITED = 'invited';

/** Scheda dei ticket di altri canali in copia ai canali dell'operatore (parte A). */
export const TAB_COPY = 'copy';

/**
 * Schede della coda. «Invitato» solo nell'area rappresentante (la spec la vuole
 * nelle liste del rappresentante; lato admin gli inviti restano in «Tutti»).
 * «In copia» (parte A) anche solo lì e solo se un suo canale ha copie
 * (`hasCopies`): senza copie le schede restano quelle di prima.
 */
export const queueTabsFor = (
  kind: 'representative' | 'admin',
  opts: { hasCopies?: boolean } = {}
): { key: string; label: string }[] => [
  // Area rappresentante (28/10/2026): la scheda di partenza sta per prima e si
  // chiama per quello che mostra con lo stato di default (Attivi). Admin invariato.
  ...(kind === 'representative' ? [{ key: 'all', label: 'Aperti e in lavorazione' }] : []),
  { key: 'unassigned', label: 'Non assegnati' },
  { key: 'mine', label: 'Presi da me' },
  ...(kind === 'representative' ? [{ key: TAB_INVITED, label: 'Invitato' }] : []),
  ...(kind === 'representative' && opts.hasCopies ? [{ key: TAB_COPY, label: 'In copia' }] : []),
  ...(kind === 'admin' ? [{ key: 'all', label: 'Tutti del canale' }] : []),
  { key: TAB_CLOSED, label: 'Risolti e chiusi' },
];

/** Almeno un canale dell'operatore riceve ticket in copia (`/groups` → `mine[i].copyCount`, parte A). */
export const hasCopyQueue = (res: { mine?: { copyCount?: number }[] }): boolean =>
  (res.mine || []).some((g) => (g.copyCount || 0) > 0);

/**
 * Il rappresentante ha una coda di supporto (voce «Supporto» del menu, pagina
 * senza l'avviso «Nessun canale»): componente di un canale, assegnatario
 * esterno (`assignedCount`, Task 26), invitato in un ticket (`invitedCount`,
 * parte B) o con ticket in copia ai suoi canali (`copyCount`, parte A).
 */
export const hasSupportQueue = (res: { mine?: unknown[]; assignedCount?: number; invitedCount?: number }): boolean =>
  (res.mine || []).length > 0 ||
  (res.assignedCount || 0) > 0 ||
  (res.invitedCount || 0) > 0 ||
  hasCopyQueue(res as { mine?: { copyCount?: number }[] });

/**
 * Ticket per pagina della coda (sito e admin): 100, il massimo che accetta il
 * backend (`listTickets`, supportTicketService.js). Prima erano 20 (default del
 * backend): con 100 la coda si apre quasi sempre su un'unica pagina.
 */
export const QUEUE_PAGE_SIZE = 100;

/**
 * Parametri della `listTickets`, identici a quelli che compone oggi
 * `TicketQueue.tsx:231-242`: i campi vuoti diventano `undefined` (il backend
 * li ignora), `search` e `tag` vengono ripuliti dagli spazi.
 */
export const buildListParams = (
  f: QueueFilterState,
  ctx: {
    tab: string;
    search: string;
    page: number;
    quick?: QueueQuickFilter;
    directOnly?: boolean;
    /** false = stato di default mai scelto: con una ricerca non si manda (tutti gli stati). Assente = scelto. */
    statusChosen?: boolean;
    /** Ordinamento di colonna; assente/null = ordine di sempre (nessun parametro). */
    columnSort?: ColumnSort | null;
  }
): ListParams => ({
  tab: ctx.tab === TAB_CLOSED ? 'all' : ctx.tab,
  // Sul tab dei conclusi il filtro stato non si applica: e il tab a fissarlo.
  status: ctx.tab === TAB_CLOSED
    ? CLOSED_STATUSES
    : searchesAllStates(f.status, ctx)
      ? undefined
      : (f.status || undefined),
  priority: f.priority || undefined,
  groupId: f.groupId || undefined,
  search: ctx.search.trim() || undefined,
  origin: f.origin || undefined,
  tag: f.tag.trim() || undefined,
  requesterType: f.requesterType || undefined,
  unread: f.unreadOnly || undefined,
  hideNoise: f.hideNoise || undefined,
  // Viste admin (Task 31): assenti = parametri identici a prima.
  dispatch: ctx.quick === 'dispatch' || undefined,
  unassignedOpen: ctx.quick === 'unassignedOpen' || undefined,
  directOnly: ctx.directOnly || undefined,
  // Chip/pillola «Bozze da approvare» (Task 46).
  pendingOnly: ctx.quick === 'pending' || undefined,
  sort: ctx.columnSort ? ctx.columnSort.key : undefined,
  dir: ctx.columnSort ? ctx.columnSort.dir : undefined,
  page: ctx.page,
  limit: QUEUE_PAGE_SIZE,
});

/**
 * Stato dei filtri prodotto dall'applicazione di una vista salvata
 * (trasporto invariato di `TicketQueue.tsx:145-158`):
 *  - la vista vince sul default `active`: se non fissa lo stato si torna a
 *    "Tutti gli stati", che e cio che l'utente vedeva al salvataggio;
 *  - la vista non memorizza il filtro sui non letti, quindi applicarla lo
 *    azzera: resterebbe altrimenti attivo un filtro invisibile nella vista.
 */
export const filtersFromView = (
  v: SavedView
): { tab: string; search: string } & Pick<QueueFilterState, 'status' | 'priority' | 'groupId' | 'unreadOnly'> => ({
  tab: v.filtri.tab || 'all',
  status: v.filtri.status || '',
  priority: v.filtri.priority || '',
  groupId: v.filtri.groupId || '',
  search: v.filtri.search || '',
  unreadOnly: false,
});

/** Filtri memorizzati in una nuova vista (`TicketQueue.tsx:164-170`). */
export const viewFiltersFromState = (
  f: QueueFilterState,
  ctx: { tab: string; search: string }
): SavedViewFilters => ({
  tab: ctx.tab,
  status: f.status || undefined,
  priority: f.priority || undefined,
  groupId: f.groupId || undefined,
  search: ctx.search.trim() || undefined,
});

/** Una vista si salva solo con un nome non vuoto (`TicketQueue.tsx:161-162`). */
export const isValidViewName = (nome: string): boolean => nome.trim().length > 0;

const PRIORITY_RANK: Record<string, number> = { urgente: 0, alta: 1, media: 2, bassa: 3 };

const isConcluded = (t: TicketListItem): boolean => t.status === 'risolto' || t.status === 'chiuso';

/**
 * Riordino della SOLA pagina corrente. Il backend ordina per
 * `unread.group desc, lastActivityAt desc` (supportTicketService.js): il toggle
 * del prototipo (L606) riordina quindi cio che e gia a schermo, non l'intera
 * coda. Con un ordinamento di colonna (lato server) la coda NON chiama questa
 * funzione. `activeFirst` (con una ricerca): i non conclusi restano in cima,
 * come li ha messi il backend.
 */
export const sortRowsClientSide = (
  items: TicketListItem[],
  sort: QueueSort,
  opts: { activeFirst?: boolean } = {}
): TicketListItem[] =>
  [...items].sort((a, b) => {
    if (opts.activeFirst) {
      const ac = isConcluded(a) ? 1 : 0;
      const bc = isConcluded(b) ? 1 : 0;
      if (ac !== bc) return ac - bc;
    }
    // "Non letto" = messaggio del richiedente O nota interna per me: stesso
    // criterio dell'ordinamento del backend (Task 23/B).
    const au = a.unread?.group || a.unreadNoteForMe ? 0 : 1;
    const bu = b.unread?.group || b.unreadNoteForMe ? 0 : 1;
    if (sort === 'priority') {
      const ap = PRIORITY_RANK[a.priority] ?? 9;
      const bp = PRIORITY_RANK[b.priority] ?? 9;
      if (ap !== bp) return ap - bp;
    }
    if (au !== bu) return au - bu;
    return new Date(b.lastActivityAt).getTime() - new Date(a.lastActivityAt).getTime();
  });

/**
 * Avatar mostrati in riga prima del "+N" (parita con `TicketQueue.tsx:723-738`,
 * che ne mostra 3 e poi il resto come contatore).
 */
const MAX_AVATARS = 3;

/** Cio che serve a disegnare una riga di coda: nessun calcolo nel componente. */
export interface QueueRowModel {
  id: string;
  number: string;
  subject: string;
  /** 700 quando c'e un messaggio non letto: e il "grassetto" della coda di oggi. */
  subjectWeight: 700 | 500;
  unread: boolean;
  /** Nota interna da leggere per l'operatore corrente: pallino e badge ambra. */
  unreadNote: boolean;
  age: string;
  /** Data di apertura (`createdAt`) in breve, gg/mm/aaaa: colonna «Aperto» della tabella. */
  openedAt: string;
  requesterName: string;
  /**
   * Nome del richiedente nella tabella: nominativo, altrimenti nome o email del
   * richiedente, e per l'ospite senza nulla «Contatto esterno». La card resta su
   * `requesterName`.
   */
  requesterLabel: string;
  /** Tag di tipo sotto il nome, in tabella (Ospite, Studio, Autoscuola, …); null = nessuno. */
  requesterTag: RequesterTag | null;
  status: S3Visual;
  priority: S3Visual;
  origin: S3Visual;
  /** Associato / Ospite / Utente interno (ruling 18). Etichetta vuota se assente. */
  requesterType: S3Visual;
  /** Stelle della valutazione dell'associato; `null` finche il ticket non e stato valutato. */
  rating: number | null;
  tags: string[];
  railColor: string;
  groupName: string;
  /** Iniziali, nomi per esteso e chiavi stabili dei primi `MAX_AVATARS` assegnatari. */
  assigneeInitials: string[];
  /** Nominativo completo: e il `title` dell'avatar (parita TicketQueue.tsx:728). */
  assigneeNames: string[];
  /** `actorType:actorId`, chiave di lista identica a quella di oggi (TicketQueue.tsx:726). */
  assigneeKeys: string[];
  /**
   * Foto degli stessi assegnatari (URL assoluto) o `undefined`: i rappresentanti
   * ce l'hanno, gli utenti admin no. Allineato per indice agli array sopra.
   */
  assigneePhotos: (string | undefined)[];
  extraAssignees: number;
  selected: boolean;
  // ── Campi per la veste v4 (T34): aggiunte pure, la v3 non li legge. ──
  /** Chiave grezza dello stato (glifo v4, N11). */
  statusKey: string;
  /** Stato mostrato: «Unito» / «Indesiderato» al posto di «Chiuso» (`displayStatus`). */
  statusDisplay: DisplayStatus;
  /** Chiave grezza della priorita (barre v4, N12). */
  priorityKey: string;
  /** Canale per il colore deterministico (N10); `inboundDefault` dai canali caricati. */
  channel: { _id?: string; nome: string; inboundDefault?: boolean; avatar?: ChannelAvatarLike | null } | null;
  /** Ticket non risolto/chiuso: in v4 le righe non attive vanno al 60% (N15). */
  active: boolean;
  /** Allineato agli avatar mostrati: true sull'utente corrente (anello verde, N14). */
  assigneeSelf: boolean[];
  /** `Tag: #a #b` per il `title` dell'oggetto (CT-39, RA-06); undefined senza tag. */
  tagsTitle?: string;
  /** Bozze di risposta in attesa di approvazione (Task 46); 0 = nessun badge. */
  pendingDrafts: number;
}

/** Nome mostrato dalla tabella (vedi `QueueRowModel.requesterLabel`). */
export const tableRequesterLabel = (t: Pick<TicketListItem, 'associateSnapshot' | 'requester'>): string => {
  const first = [t.associateSnapshot?.nominativo, t.requester?.nome, t.requester?.email]
    .map((v) => (v || '').trim())
    .find(Boolean);
  if (first) return first;
  return t.requester?.type === 'guest' ? 'Contatto esterno' : '';
};

export type RequesterTagKind = 'ospite' | 'studio' | 'autoscuola' | 'nautica' | 'interno';
export interface RequesterTag {
  kind: RequesterTagKind;
  label: string;
}

/** Categorie standard dell'anagrafica (Associate.categoria, senza enum: valori dall'Excel). */
const CATEGORY_TAGS: Record<string, RequesterTag> = {
  STUDIO: { kind: 'studio', label: 'Studio' },
  AUTOSCUOLA: { kind: 'autoscuola', label: 'Autoscuola' },
  NAUTICA: { kind: 'nautica', label: 'Nautica' },
};

/**
 * Tag di tipo del richiedente (tabella): ospite → «Ospite»; associato → la sua
 * categoria se e una delle tre standard (altrimenti, o senza categoria, nessun
 * tag); utente interno → «Utente interno». Funzione pura.
 */
export const requesterTagOf = (t: Pick<TicketListItem, 'requester' | 'associateCategoria'>): RequesterTag | null => {
  const type = t.requester?.type;
  if (type === 'guest') return { kind: 'ospite', label: 'Ospite' };
  if (type === 'user') return { kind: 'interno', label: 'Utente interno' };
  const cat = (t.associateCategoria || '').trim().toUpperCase();
  return CATEGORY_TAGS[cat] || null;
};

/** Testo del badge delle bozze in riga (Task 46). */
export const pendingDraftsLabel = (n: number): string => (n === 1 ? '1 bozza' : `${n} bozze`);

export const buildQueueRow = (
  t: TicketListItem,
  ctx: { selectedTicketId?: string; now?: Date; selfId?: string; defaultGroupIds?: ReadonlySet<string> } = {}
): QueueRowModel => {
  const assignees = t.assignees || [];
  const shown = assignees.slice(0, MAX_AVATARS);
  const unread = Boolean(t.unread?.group);
  const unreadNote = Boolean(t.unreadNoteForMe);
  return {
    id: t._id,
    number: t.ticketNumber,
    subject: t.subject,
    subjectWeight: unread || unreadNote ? 700 : 500,
    unread,
    unreadNote,
    age: relativeAge(t.lastActivityAt, ctx.now),
    openedAt: shortDateIt(t.createdAt),
    requesterName: t.associateSnapshot?.nominativo || '',
    requesterLabel: tableRequesterLabel(t),
    requesterTag: requesterTagOf(t),
    status: statusVisual(t.status),
    priority: priorityVisual(t.priority),
    origin: originVisual(t.origin),
    requesterType: requesterTypeVisual(t.requester?.type),
    rating: typeof t.rating?.stars === 'number' ? t.rating.stars : null,
    tags: t.tags || [],
    railColor: priorityRailColor(t.priority),
    groupName: t.groupId?.nome || '',
    assigneeInitials: shown.map((a) => initialsOf(a.nome)),
    // Tooltip: l'assegnatario esterno al canale (Task 26) si riconosce passandoci sopra.
    assigneeNames: shown.map((a) => (a.external ? `${a.nome} · Esterno al canale` : a.nome)),
    assigneeKeys: shown.map((a) => `${a.actorType}:${a.actorId}`),
    assigneePhotos: shown.map((a) => photoUrl(a.foto)),
    extraAssignees: Math.max(0, assignees.length - MAX_AVATARS),
    selected: Boolean(ctx.selectedTicketId && ctx.selectedTicketId === t._id),
    statusKey: t.status,
    statusDisplay: displayStatus(t),
    priorityKey: t.priority,
    channel: t.groupId
      ? {
          _id: t.groupId._id,
          nome: t.groupId.nome,
          inboundDefault: Boolean(t.groupId._id && ctx.defaultGroupIds?.has(t.groupId._id)),
          // Avatar del canale (emoji/immagine): solo se presente, la forma di sempre resta identica.
          ...(t.groupId.avatar ? { avatar: t.groupId.avatar } : {}),
        }
      : null,
    active: t.status !== 'risolto' && t.status !== 'chiuso',
    assigneeSelf: shown.map((a) => Boolean(ctx.selfId) && String(a.actorId) === String(ctx.selfId)),
    tagsTitle: t.tags && t.tags.length ? `Tag: ${t.tags.map((x) => `#${x}`).join(' ')}` : undefined,
    pendingDrafts: Math.max(0, Number(t.pendingRepliesCount) || 0),
  };
};
